import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Subject, Subscription, takeUntil, tap } from 'rxjs';
import { toggle } from 'src/app/shared/store/shared.actions';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { CommonService } from 'src/app/shared/common.service';
import { NavigationService } from 'src/app/shared/router.service';
import { initNavbarParams } from 'src/app/shared/utils/helpers';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { RealtimeService, UserEvent } from 'src/app/features/smart-inbox/services/realtime.service';
import * as smartInboxActions from 'src/app/features/smart-inbox/store/smart-inbox.actions';
import * as aiChatActions from 'src/app/features/ai-chat/store/ai-chat.actions';
import * as aiChatSelectors from 'src/app/features/ai-chat/store/ai-chat.selectors';
import * as notificationActions from 'src/app/features/notifications/store/notification.actions';
import * as ordersActions from 'src/app/features/orders/store/orders.actions';
import * as quotationsActions from 'src/app/features/quotations/store/quotations.actions';

@Component({
  selector: 'app-main-page',
  templateUrl: './main-page.component.html',
  styleUrls: ['./main-page.component.scss'],
})
export class MainPageComponent implements OnInit {

  toggle!: boolean;
  aiChatDrawerOpen = false;

  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private actions$: Actions,
    private router: Router,
    private translateService: TranslateService,
    private navigationService: NavigationService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private realtimeService: RealtimeService,
    private commonService: CommonService,
  ) {}

  ngOnInit(): void {
    const sub1 = this.actions$
      .pipe(
        ofType(toggle),
        tap((action) => {
          this.toggle = action.toggle;
        })
      )
      .subscribe();
    this.subscriptions.push(sub1);

    this.store
      .select(aiChatSelectors.selectAiChatDrawerOpen)
      .pipe(takeUntil(this.destroy$))
      .subscribe((open) => (this.aiChatDrawerOpen = open));

    this.connectUserEvents();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
    this.realtimeService.disconnectUserEvents();
  }

  resetMarginLeft(): boolean {
    const routesToResetMarginLeft = [
      '/quotations/add',
      '/settings',
    ];

    return !routesToResetMarginLeft.some((route) =>
      this.router.url.startsWith(route)
    );
  }

  onActivate(e:any): void{
    const currentActivatedRoute: ActivatedRoute | null = this.getCurrentActivatedRoute();
    this.navigationService.setCurrentRoute(currentActivatedRoute);
    initNavbarParams(this.store, currentActivatedRoute);
  }

  onDeactivate(e:any): void{
    initNavbarParams(this.store, null, true);
  }

  getCurrentActivatedRoute(): ActivatedRoute | null {
    let route = this.activatedRoute.firstChild;
    while (route?.firstChild) {
      route = route?.firstChild;
    }
    return route
  }

  
  closeAiChatDrawer(): void {
    this.store.dispatch(aiChatActions.SetAiChatDrawerOpen({ open: false }));
  }

  private connectUserEvents(): void {
    this.realtimeService
      .connectUserEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event: UserEvent) => {
          if (event.type === 'connected') {
            this.store.dispatch(
              notificationActions.SetUnreadCount({ count: event.unread_count ?? 0 })
            );
            return;
          }

          if (event.type === 'comment_mention') {
            this.store.dispatch(
              smartInboxActions.CommentMentionReceived({
                threadId: event.thread_id,
                commentId: event.comment_id,
                threadSubject: event.thread_subject
              })
            );

            if (event.notification_id) {
              this.store.dispatch(notificationActions.IncrementUnreadCount());
            }
          }

          if (event.type === 'thread_assignment') {
            this.store.dispatch(
              smartInboxActions.ThreadAssignmentReceived({
                threadIds: event.thread_ids,
                authorId: event.author_id,
                timestamp: event.timestamp
              })
            );

            if (event.notification_id) {
              this.store.dispatch(notificationActions.IncrementUnreadCount());
            }
          }

          if (event.type === 'order_created') {
            this.store.dispatch(
              smartInboxActions.OrderCreatedReceived({
                threadId: event.thread_id,
                threadSubject: event.thread_subject,
              })
            );

            if (event.notification_id) {
              this.store.dispatch(notificationActions.IncrementUnreadCount());
            }
          }

          if (event.type === 'order_creation_failed') {
            // SSE-only, no persisted notification: clear the skeleton — without
            // this it would spin forever on a failed job — and say why, since a
            // placeholder vanishing on its own reads as nothing having happened.
            // The toast belongs to `createOrderError$`, the one owner for both
            // failure paths; toasting here as well produced two for one failure.
            this.store.dispatch(
              smartInboxActions.CreateOrderError({
                conversationId: event.thread_id,
                errorCode: event.error_code ?? null,
              }),
            );
          }

          if (event.type === 'order_status_changed') {
            this.store.dispatch(quotationsActions.RefreshQuotations());
            this.store.dispatch(ordersActions.RefreshOrders());

            if (event.notification_id) {
              this.store.dispatch(notificationActions.IncrementUnreadCount());
            }
          }

          if (event.type === 'erp_sync_status_changed') {
            // Patched in place (list row + open detail page), not a refetch: the event
            // already carries the full outcome, and Connect's callback settles this on a
            // request the viewing user has no part in, so a live patch is the only way
            // the badge/error banner catch up without a manual reload.
            this.store.dispatch(
              quotationsActions.ErpSyncStatusChanged({
                id: event.order_id,
                sync_status: event.sync_status,
                error_type: event.error_type ?? null,
                error: event.error ?? null,
                erp_message: event.erp_message ?? null,
              })
            );
          }

          if (event.type === 'workflow_send_reply_failed') {
            // A send_reply automation drafted instead of sending — bump the
            // notification badge so the mailbox owner sees it needs review.
            if (event.notification_id) {
              this.store.dispatch(notificationActions.IncrementUnreadCount());
            }
          }
          this.store.dispatch(notificationActions.UserEventReceived({ event }));
        },
        error: (err) => {
          console.error('[Inbox] User-events connection error:', err);
        },
      });
  }
}
